<template>
    <div id="full-page-map" class="tracking-map">
        <div id="map-container">

            <div id="drivers">
                <ul id="drivers-list" class="ps-0" ref="driversList">
                </ul>
                <button @click="resetZoom">Reset Zoom</button>
            </div>

            <div ref="mapElement" style="height: 100%"></div>
        </div>
    </div>
</template>

<style>

#full-page-map #main-container,
#full-page-map #map-container {
    height: 100vh;
    width: 100vw;
}

#full-page-map #map-container {
    position: relative;
}

.filters label {
    display: block;
    margin-top: 4px;
}

.tracking-map #drivers {
    font-family:"Open Sans";
    font-size:14px;
    background:white;
    padding:10px;
    position:absolute;
    left:10px;
    top:50px;
    z-index: 1000;
}

.tracking-map #drivers-list {
    list-style:none;
    margin-bottom:20px;
}

.tracking-map #drivers-list span:first-child {
    border:1px solid black;
    display:inline-block;
    height: 10px;
    width: 10px;
}

.tracking-map #drivers-list span:last-child {
    font-size:10px;
    font-style:italic;
}

.tracking-map #drivers button {
    background:#c2c2c2;
    border:0;
    border-radius:3px;
    color:white;
    line-height:1.5;
    padding:5px 10px;
}

@keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-15px); }
    60% { transform: translateY(-7px); }
}

// Needed for animation of the notification and notification circle. These are used.
.tracking-map .marker-bounce {
    animation: bounce 0.6s ease;
}

.tracking-map .notification-circle {
    position: absolute;
    width: 0;
    height: 0;
    margin: 0;
    border-radius: 50%;
    background: rgba(0,0,0,0.5);
    border: 1px solid rgba(0,0,0,1);
    pointer-events: none;
    transform-origin: center center;
}

</style>

<script>
import { Loader } from '@googlemaps/js-api-loader';
import { useEchoModel } from "@laravel/echo-vue";
import {
    defaultCenter,
    defaultZoom,
    selectColourH,
    makeHSL,
} from "@/tracking/mapUtils.js";

export default {
    props: {
        devices: {
            type: Array,
            required: true
        },
    },

    data() {
        return {
            routes: {},
            startMarkers: {},
            finishMarkers: {},
            directionsService: null,

            filterAssigned: true,
            filterUnassigned: true,

            colourIterator: 0,

            liveDrivers: [],

            loader: new Loader({
                apiKey: 'AIzaSyD_tfgEUWxTtVczG5euJnkbeZ9DryHp4F4',
                version: 'weekly',
                libraries: ['maps', 'marker']
            })
        };
    },

    async mounted() {
        await this.loader.load();
        this.initMap();
    },

    methods: {

        zoomToDriver(driver) {
            this.map.setCenter(driver.marker.position);
            this.map.setZoom(16);
        },

        resetZoom() {
            this.map.setCenter(defaultCenter);
            this.map.setZoom(defaultZoom);
        },

        animateNotificationCircleDiv(el, scale) {
            return () => {
                if (scale === 1) {
                    // reset back to zero state
                    el.style.width = '0px';
                    el.style.height = '0px';
                    el.style.opacity = '0';
                    el.style.marginLeft = '0px';
                    el.style.marginTop = '0px';
                } else {
                    // same math as before
                    const strokeOpacity = Math.cos((Math.PI / 2) * scale);
                    const fillOpacity = strokeOpacity * 0.5;
                    const r = Math.sin((Math.PI / 2) * scale) * 30; // 30px max radius

                    el.style.opacity = fillOpacity;
                    el.style.width = `${r * 2}px`;
                    el.style.height = `${r * 2}px`;
                    el.style.marginLeft = `${-r}px`;
                    el.style.marginTop = `${-r}px`;
                }
            }
        },

        async loadLibraries() {

            const imports = [
                "maps",
                "marker",
                "geocoding",
                "core",
                "routes",
            ];

            const [maps, marker, geocoding, core, routes] = await Promise.all(
                imports.map(lib => google.maps.importLibrary(lib))
            );

            const {
                Map,
                InfoWindow,
                StyledMapType,
                Polyline
            } = maps;

            const {
                AdvancedMarkerElement,
                PinElement
            } = marker;

            const {
                Geocoder
            } = geocoding;

            const {
                LatLng,
                LatLngBounds,
                UnitSystem
            } = core;

            const {
                DirectionsService,
                DirectionsStatus,
                TravelMode,
                DirectionsRenderer
            } = routes;

            return {
                Map,
                AdvancedMarkerElement,
                StyledMapType,
                LatLngBounds,
                Polyline,
                PinElement,
                Geocoder,
                LatLng,
                DirectionsService,
                DirectionsStatus,
                TravelMode,
                UnitSystem,
                InfoWindow,
                DirectionsRenderer
            };

        },

        async initMap() {

            const {
                Map,
                AdvancedMarkerElement,
                LatLng,
                LatLngBounds,
                Polyline,
                DirectionsService,
                PinElement,
                StyledMapType
            } = await this.loadLibraries();

            const styleArray = [
                {featureType: "administrative", elementType: "all", stylers: [{visibility: "off"}]},
                {featureType: "landscape", elementType: "geometry", stylers: [{visibility: "off"}]},
                {
                    featureType: "landscape.natural",
                    elementType: "geometry",
                    stylers: [{visibility: "on"}, {color: "#ffffff"}]
                },
                {featureType: "landscape.natural.landcover", elementType: "geometry", stylers: [{visibility: "off"}]},
                {featureType: "poi", elementType: "all", stylers: [{visibility: "off"}]},
                {featureType: "road", elementType: "labels.text.fill", stylers: [{color: "#a05301"}]},
                {featureType: "road", elementType: "labels.text.stroke", stylers: [{color: "#ffffff"}]},
                {featureType: "road.highway", elementType: "geometry.fill", stylers: [{color: "#a4a4a4"}]},
                {featureType: "road.highway", elementType: "geometry.stroke", stylers: [{visibility: "off"}]},
                {
                    featureType: "road.arterial",
                    elementType: "geometry.fill",
                    stylers: [{color: "#cecece"}, {visibility: "on"}]
                },
                {featureType: "road.local", elementType: "geometry.fill", stylers: [{color: "#e2e2e2"}]},
                {featureType: "transit.line", elementType: "geometry", stylers: [{visibility: "off"}]},
                {featureType: "transit.station", elementType: "geometry", stylers: [{visibility: "off"}]},
                {featureType: "water", elementType: "all", stylers: [{color: "#46bcec"}]},
                {featureType: "water", elementType: "geometry", stylers: [{color: "#dddddd"}]},
                {featureType: "water", elementType: "geometry.fill", stylers: [{color: "#c8d7d4"}]}
            ];
            const styledMapType = new StyledMapType(styleArray, {name: 'Custom Style'});

            this.map = new Map(this.$refs.mapElement, {
                mapId: 'DEMO_MAP_ID', // use a random mapId to avoid conflicts
                center: {
                    lat: defaultCenter.lat,
                    lng: defaultCenter.lng
                },
                zoom: 5,
                fullscreenControl: false,
                scrollwheel: false,
                mapTypeControlOptions: {
                    mapTypeIds: ['roadmap', 'styled_map']
                },
            });

            this.map.mapTypes.set('styled_map', styledMapType);
            this.map.setMapTypeId('styled_map');

            this.directionsService = new DirectionsService();

            const bounds = new LatLngBounds();

            this.$refs.driversList.innerHTML = '';

            this.devices.forEach(device => {

                const driver = device.driver;

                const sortedRaw = [...device.waypoints].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                const latestThree = sortedRaw.slice(0, 3);

                const waypoints = latestThree.map(wp => new LatLng(wp.lat, wp.lng));
                const latestWaypoint = waypoints[0];

                driver.colour = {
                    h: selectColourH(++this.colourIterator, this.devices.length),
                    s: 100,
                    l: 50
                };

                driver.rawWaypoints = sortedRaw;
                driver.waypoints = waypoints;

                driver.polyline = new Polyline({
                    clickable: false,
                    geodesic: true,
                    map: this.map,
                    path: waypoints,
                    strokeColor: makeHSL(driver.colour),
                    strokeOpacity: 1,
                    strokeWeight: 2
                });

                const pin = new PinElement({
                    glyph: '',
                    background: makeHSL(driver.colour),
                    borderColor: '#000',
                }).element;

                driver.marker = new AdvancedMarkerElement({
                    map: this.map,
                    position: latestWaypoint,
                    title: driver.name,
                    content: pin,
                });

                driver.pin = pin;

                const notificationElement = document.createElement('div');
                notificationElement.classList.add('notification-circle');

                notificationElement.style.backgroundColor = makeHSL(driver.colour);
                notificationElement.style.borderColor = makeHSL(driver.colour);

                driver.notificationcircle = new AdvancedMarkerElement({
                    map:     this.map,
                    position: latestWaypoint,
                    content: notificationElement,
                });

                driver.notificationEl = notificationElement;

                const li = document.createElement('li');
                li.setAttribute('data-driver-id', driver.id);
                li.addEventListener(
                    'click',
                    this.zoomToDriver.bind(this, driver)
                );

                li.innerHTML = `
                <span style="background: ${makeHSL(driver.colour)}"></span>
                ${driver.name}
                <span>not updated</span>`;

                this.$refs.driversList.appendChild(li);

                bounds.extend(waypoints[waypoints.length - 1]);
                bounds.extend(latestWaypoint);

                useEchoModel(
                    "App.Models.Movements.Tracking.Waypoint",
                    driver.id,
                    ["WaypointCreated"],
                    this.onWaypointCreated.bind(this)
                ).listen();

                this.liveDrivers.push(device.driver);

            });

            this.map.fitBounds(bounds);

            window.addEventListener('resize', () => {
                google.maps.event.trigger(this.map, 'resize');
                this.map.fitBounds(bounds);
            });
        },

        async onWaypointCreated({ model: waypoint }) {
            const {
                LatLng,
                LatLngBounds,
            } = await this.loadLibraries();

            const driver = this.liveDrivers.find(d => d.id === waypoint.driver_id);
            if (!driver) return;

            driver.rawWaypoints.push(waypoint);

            const sortedRaw = [...driver.rawWaypoints].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            const latestThree = sortedRaw.slice(0, 3);

            const waypoints = latestThree.map(wp => new LatLng(wp.lat, wp.lng));
            const latestWaypoint = waypoints[0];

            driver.polyline.setPath(waypoints);
            driver.marker.position = latestWaypoint;

            driver.notificationcircle.position = latestWaypoint;

            const bounds = new LatLngBounds();
            this.liveDrivers.forEach(d => {
                bounds.extend(d.marker.position);
            });
            this.map.fitBounds(bounds);

            const li = this.$refs.driversList.querySelector(
                `li[data-driver-id="${driver.id}"] span:last-child`
            );

            if (li) {
                li.textContent = new Date(waypoint.created_at).toLocaleTimeString();
            }

            driver.pin.classList.add('marker-bounce');
            driver.pin.addEventListener('animationend', () => {
                driver.pin.classList.remove('marker-bounce');
            }, { once: true });

            for (let i = 0; i <= 15; i++) {
                setTimeout(
                    this.animateNotificationCircleDiv(driver.notificationEl, i/15),
                    i * 60
                );
            }

        }

    }
};
</script>
